import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Filter, ShieldCheck, Loader2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { AuditFeed } from '../components/AuditTimeline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_FILTERS = [
  { key: 'all',        label: 'All requests' },
  { key: 'correction', label: 'Payroll corrections' },
  { key: 'leave',      label: 'Time-off' },
  { key: 'advance',    label: 'Cash advances' },
];

const STATUS_FILTERS = [
  { key: 'all',                  label: 'Any status' },
  { key: 'pending',              label: 'Pending' },
  { key: 'returned_for_revision', label: 'Returned' },
  { key: 'approved',             label: 'Approved' },
  { key: 'rejected',             label: 'Rejected' },
];

const KIND_TO_PATH = {
  correction: '/payroll/corrections',
  leave:      '/time-off',
  advance:    '/time-off',
};

const AuditLogPage = () => {
  const navigate = useNavigate();
  const [kindFilter, setKindFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (kindFilter !== 'all') params.set('kind', kindFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await axios.get(`${API}/audit/stream?${params.toString()}`, { withCredentials: true });
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [kindFilter, statusFilter]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const handleOpenRequest = ({ kind }) => {
    const path = KIND_TO_PATH[kind] || '/dashboard';
    navigate(path);
  };

  const summary = useMemo(() => {
    const c = {};
    for (const e of events) { c[e.type] = (c[e.type] || 0) + 1; }
    return c;
  }, [events]);

  return (
    <DashboardLayout>
      <div className="p-8 max-w-5xl mx-auto" data-testid="audit-log-page">
        <button
          data-testid="audit-back-button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              <ShieldCheck className="w-7 h-7 text-[#002FA7]" />
              Audit Log
            </h1>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Company-wide timeline of every submission, view, comment, return, approval, and rejection.
            </p>
          </div>
          <button
            data-testid="audit-refresh"
            onClick={fetchAudit}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#002FA7] hover:bg-[#002FA7] hover:text-white px-3 py-2 border border-[#002FA7] rounded-md transition-colors disabled:opacity-50"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mr-2">Kind</span>
            {KIND_FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`audit-kind-${f.key}`}
                onClick={() => setKindFilter(f.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${kindFilter === f.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mr-2 ml-5">Status</span>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`audit-status-${f.key === 'returned_for_revision' ? 'returned' : f.key}`}
                onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${statusFilter === f.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Counts */}
        <div className="flex items-center gap-3 text-xs text-slate-600 mb-4 flex-wrap" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <span data-testid="audit-total" className="font-semibold">{total} event{total === 1 ? '' : 's'}</span>
          {Object.entries(summary).map(([t, c]) => (
            <span key={t} className="text-slate-500">· {c} {t}</span>
          ))}
        </div>

        {/* Feed */}
        {loading ? (
          <div className="text-center py-12 text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading…
          </div>
        ) : (
          <AuditFeed events={events} onOpenRequest={handleOpenRequest} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default AuditLogPage;
